// A copy of Harbor's installer (packages/integrations/harbor/frontend/src/index.tsx),
// kept apart so that LaunchDarkly changes no other integration. Changed on
// purpose: the `account` item in place of Harbor's instance, with no URL, and
// token steps on its setup page.
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import { LaunchDarklyComponents } from "@p0security/integrations-launchdarkly-shared/components";
import { toGcpConnectorServiceAccount } from "@p0security/integrations-launchdarkly-shared/connector-hosting";
import {
  INVALID_ITEM_ID,
  isItemId,
} from "@p0security/integrations-launchdarkly-shared/item-id";
import type {
  LaunchDarklyAccountConfig,
  LaunchDarklyConnectorConfig,
  LaunchDarklyIntegration,
} from "@p0security/integrations-launchdarkly-shared/types";
import type { VAULT_CLOUDS } from "@p0security/integrations-launchdarkly-shared/vault";
import { p0InvokerEmail } from "@p0security/integrations-vaulted-credential-shared/iac";
import {
  installedIds,
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { ValidationError } from "@p0security/shared/types/error";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import {
  type IacCommand,
  MISSING_SERVICE_ACCOUNT,
  REINSTALL,
  renderAwsConnector,
  renderAwsReadGrants,
  renderGcpConnector,
  renderGcpReadGrant,
  renderInvalidIdMessage,
} from "./iac.tsx";
import { toTokenSteps, vaultLabel } from "./preamble.tsx";

export type LaunchDarklyInstaller = FrontendInstaller<
  typeof LaunchDarklyComponents
>;

type LaunchDarklyInstallContext = Readonly<
  FrontendInstallContext<LaunchDarklyIntegration>
>;

type Hosting = LaunchDarklyConnectorConfig["hosting"];

type Cloud = Hosting["type"];

type HostingOf<C extends Cloud> = Extract<Hosting, { type: C }>;

type Vault = LaunchDarklyAccountConfig["vault"];

type VaultKind = Vault["type"];

/** The vault a connector in cloud `C` can read: its own cloud's secret
 * manager. */
type VaultIn<C extends Cloud> = {
  [V in VaultKind]: (typeof VAULT_CLOUDS)[V] extends C ? V : never;
}[VaultKind];

/** An account being configured: on the new item's form, any field may still
 * be missing. */
type AccountDraft = Readonly<Partial<LaunchDarklyAccountConfig>>;

const { Paragraph, Text } = Typography;

const { hosting: hostingElement } = LaunchDarklyComponents.connector.schema;

const connectorHelp = (
  <Paragraph>
    Apply the Terraform below to deploy the connector. Then click Next: P0
    checks that it can invoke the connector.
  </Paragraph>
);

/** An item, as an error report names it. */
type ItemName = Readonly<{ id: string; noun: "account" | "connector" }>;

/** What a setup page shows in place of Terraform that couldn't be rendered.
 *
 * A `ValidationError` is a refusal, whose message tells the customer what to
 * do. Anything else is P0's own mistake, which the customer can't fix: it's
 * logged as an error, which CaptureConsole sends to Sentry, and the page says
 * to contact P0. It isn't thrown, which would take the whole install page
 * down, and with it the button that deletes the item.
 */
const reportRefusal = (error: unknown, item: ItemName): ReactNode => {
  if (error instanceof ValidationError) {
    return <Paragraph type="warning">{error.message}</Paragraph>;
  }
  // The item's name, so that the report says whose Terraform it was. The
  // format string is a constant: the id is the customer's, and a `%` in it
  // must not be read as a format specifier.
  console.error(
    "P0 couldn't build the Terraform of the LaunchDarkly %s %s",
    item.noun,
    item.id,
    error
  );
  return (
    <Paragraph type="warning">
      P0 couldn&apos;t build this Terraform. That&apos;s a problem on P0&apos;s
      side: contact support@p0.dev.
    </Paragraph>
  );
};

/** A connector's setup page: its Terraform, under what to do with it, or what
 * {@link reportRefusal} shows in place of Terraform that couldn't be rendered.
 *
 * @param args.id The connector item's id
 * @param args.note Shown under the help either way
 * @param args.render Renders the Terraform, or throws
 */
const toConnectorSetup = (args: {
  id: string;
  note?: ReactNode;
  render: () => IacCommand;
}): Instructions<ReactNode> => {
  const { id, note, render } = args;
  try {
    return {
      commands: { iac: [render()] },
      help: (
        <>
          {connectorHelp}
          {note}
        </>
      ),
    };
  } catch (error) {
    return {
      help: (
        <>
          {reportRefusal(error, { id, noun: "connector" })}
          {note}
        </>
      ),
    };
  }
};

/** The Terraform that lets a connector read an account's secret, and what
 * the account's setup page says to do with it. */
type ReadGrantSetup = Readonly<{ help: ReactNode; iac: IacCommand[] }>;

/** An account's setup page: its token steps and its read grant's Terraform,
 * or what {@link reportRefusal} shows in place of Terraform that couldn't be
 * rendered. Nothing, if there's no grant to render.
 *
 * @param args.id The account item's id
 * @param args.render Renders the grant, or throws
 */
const toAccountSetup = (args: {
  id: string;
  render: () => ReadGrantSetup | undefined;
}): Instructions<ReactNode> | undefined => {
  const { id, render } = args;
  try {
    const grant = render();
    return grant
      ? { commands: { iac: grant.iac }, help: grant.help }
      : undefined;
  } catch (error) {
    return { help: reportRefusal(error, { id, noun: "account" }) };
  }
};

/** The service account a GCP connector runs as. P0 sets it when it saves the
 * connector. Before that it's named the same way, after the connector's
 * project and name. A stored item isn't typed by its schema, so a field that
 * isn't a string counts as missing. */
const serviceAccountOf = (hosting: HostingOf<"gcp">) => {
  const { connectorName, connectorServiceAccount, projectId } = hosting;
  if (typeof connectorServiceAccount === "string" && connectorServiceAccount) {
    return connectorServiceAccount;
  }
  return typeof connectorName === "string" &&
    connectorName &&
    typeof projectId === "string" &&
    projectId
    ? toGcpConnectorServiceAccount({ connectorName, projectId })
    : undefined;
};

/** What differs between the clouds a connector can run in. */
type CloudFacts<C extends Cloud> = Readonly<{
  /** The setup page of a connector in this cloud */
  connectorSetup: (args: {
    context: LaunchDarklyInstallContext;
    hosting: HostingOf<C>;
    id: string;
  }) => Instructions<ReactNode>;
  /** The connector's runtime, as the hosting field names it */
  label: string;
  /** Where a connector in this cloud runs, for a picker's option */
  place: (hosting: HostingOf<C>) => string;
  /** The Terraform that lets a connector in this cloud read an account's
   * secret, under the account's token steps, or nothing for a vault it
   * can't read. It throws a `ValidationError` for an account it can't be
   * rendered for. */
  readGrant: (args: {
    connectorId: string;
    hosting: HostingOf<C>;
    id: string;
    secretName: string;
    vault: Vault;
  }) => ReadGrantSetup | undefined;
  /** The one vault the connector can read: it reads an account's secret as
   * itself, which only its own cloud's secret manager accepts */
  vault: VaultIn<C>;
}>;

const CLOUDS: { readonly [C in Cloud]: CloudFacts<C> } = {
  aws: {
    connectorSetup: ({ context, hosting, id }) =>
      toConnectorSetup({
        id,
        render: () => renderAwsConnector({ context, hosting, id }),
      }),
    label: hostingElement.options.aws.label,
    place: (hosting) => `AWS account ${hosting.accountId}`,
    readGrant: ({ connectorId, hosting, id, secretName, vault }) => {
      if (vault.type !== "aws-sm") return undefined;
      return {
        help: toTokenSteps({ secretName, vault: vault.type }),
        // One snippet, or three for a secret in another AWS account than the
        // connector's.
        iac: renderAwsReadGrants({
          connectorId,
          hosting,
          id,
          secretName,
          vault,
        }),
      };
    },
    vault: "aws-sm",
  },
  gcp: {
    connectorSetup: ({ context, hosting, id }) => {
      const serviceAccount = serviceAccountOf(hosting);
      // The configure form hides the account, so this page names it, with or
      // without the Terraform.
      const serviceAccountNote = (
        <Paragraph>
          Service account:{" "}
          {serviceAccount ? (
            <Text code copyable>
              {serviceAccount}
            </Text>
          ) : (
            <Text type="warning">{MISSING_SERVICE_ACCOUNT}</Text>
          )}
          . The connector runs as it, and each LaunchDarkly account that uses
          the connector grants it read access to that account&apos;s secret.
        </Paragraph>
      );
      return toConnectorSetup({
        id,
        note: serviceAccountNote,
        render: () =>
          renderGcpConnector({
            hosting,
            id,
            invokerEmail: p0InvokerEmail(context.installed),
          }),
      });
    },
    label: hostingElement.options.gcp.label,
    place: (hosting) => `Google Cloud project ${hosting.projectId}`,
    readGrant: ({ connectorId, hosting, id, secretName, vault }) =>
      vault.type === "gcp-sm"
        ? {
            help: toTokenSteps({ secretName, vault: vault.type }),
            iac: [
              renderGcpReadGrant({
                connectorId,
                hosting,
                id,
                secretName,
                vault,
              }),
            ],
          }
        : undefined,
    vault: "gcp-sm",
  },
};

/** The facts of the cloud `type`, typed to take only that cloud's hosting
 * (or, for a union of clouds, any of theirs). */
const cloudOf = <C extends Cloud>(type: C): CloudFacts<C> => CLOUDS[type];

/** Shown instead of Terraform for an id whose Terraform names can't be built
 * from it. A connector's id is checked when it's created, and an account's
 * only by its assembler. */
const invalidIdInstructions = (
  noun: keyof typeof REINSTALL
): Instructions<ReactNode> => ({
  help: <Text type="warning">{renderInvalidIdMessage(noun)}</Text>,
});

/** The connector item the account being configured names, if it's
 * installed. */
const connectorOf = (
  context: LaunchDarklyInstallContext,
  item: AccountDraft
): LaunchDarklyConnectorConfig | undefined => {
  const { config } = context;
  return config && typeof item.connector === "string"
    ? installedItem("connector", config, item.connector)
    : undefined;
};

const isConnectorEntry = (
  entry: [string, LaunchDarklyConnectorConfig | undefined]
): entry is [string, LaunchDarklyConnectorConfig] => entry[1] !== undefined;

/** The installed connectors, for an account's `connector` field.
 *
 * Read from this integration's own live config rather than re-fetched: the
 * pointer targets a sibling component, so `otherInstalledIntegration` would
 * spend a Firestore read on a document the install context already holds.
 * Not `integrationOptionProvider` either, which leaves out ids that are
 * already items of a component: that's for pickers of a new item's id.
 */
const connectorOptions = async (context: LaunchDarklyInstallContext) => {
  const { config } = context;
  if (!config) return [];
  return Object.entries(installedItems("connector", config))
    .filter(isConnectorEntry)
    .map(([id, connector]) => {
      const { hosting } = connector;
      const name = connector.label ? `${connector.label} (${id})` : id;
      return {
        id,
        label: `${name}, in ${cloudOf(hosting.type).place(hosting)}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Why `vault` can't hold the secret of the account being configured, if its
 * connector runs in another cloud. */
const vaultPrerequisite =
  (vault: VaultKind) =>
  async (
    context: LaunchDarklyInstallContext,
    _id: string,
    item: AccountDraft
  ) => {
    const type = connectorOf(context, item)?.hosting.type;
    if (!type) return undefined;
    const cloud = cloudOf(type);
    return cloud.vault === vault
      ? undefined
      : `The connector runs on ${cloud.label}, so it can read secrets only in ${vaultLabel(cloud.vault)}.`;
  };

/** The vault field's description, or a warning when the chosen vault isn't
 * in the connector's cloud. */
const vaultDescription = (
  context: LaunchDarklyInstallContext,
  item: AccountDraft
): ReactNode => {
  const type = connectorOf(context, item)?.hosting.type;
  const chosen = item.vault?.type;
  if (type && chosen && cloudOf(type).vault !== chosen) {
    // TODO(INT-448): drop once the form stops leaving a disabled option
    // chosen. The form picks AWS Secrets Manager before a connector is
    // chosen, and keeps it, disabled, when the connector runs on Google
    // Cloud.
    const cloud = cloudOf(type);
    return (
      <Text type="warning">
        The connector runs on {cloud.label}. Choose {vaultLabel(cloud.vault)}.
      </Text>
    );
  }
  return "Where the secret holding the LaunchDarkly access token is stored. It has to be in the connector's cloud, and can be in another AWS account or Google Cloud project.";
};

/** A picker of the installs of another integration's `iam-write` component,
 * described, or told to install one first when there are none. */
const installPicker = (args: {
  description: string;
  integration: string;
  missing: string;
}) => {
  const { description, integration, missing } = args;
  return {
    labeler: (context: LaunchDarklyInstallContext) => {
      const config = context.installed[integration];
      // An installed integration's config isn't typed by its components.
      return config &&
        installedIds<string, NewConfig>("iam-write", config).length > 0
        ? description
        : missing;
    },
    optionProvider: otherInstalledIntegration({
      component: "iam-write",
      integration,
    }),
  };
};

const connectorInstructions: LaunchDarklyInstaller["components"]["connector"]["instructions"] =
  (context, id, item): Instructions<ReactNode> => {
    if (!isItemId(id)) return invalidIdInstructions("connector");
    const { hosting } = item;
    return cloudOf(hosting.type).connectorSetup({ context, hosting, id });
  };

const accountInstructions: LaunchDarklyInstaller["components"]["account"]["instructions"] =
  (context, id, item): Instructions<ReactNode> => {
    if (!isItemId(id)) return invalidIdInstructions("account");
    if (typeof item.connector !== "string" || !item.connector) {
      return {
        help: (
          <Text type="warning">
            This account names no connector. {REINSTALL.account}.
          </Text>
        ),
      };
    }
    const connector = connectorOf(context, item);
    if (!connector) {
      return {
        help: (
          <Text type="warning">
            Install a connector with the ID {item.connector}, or finish
            installing it, then return here.
          </Text>
        ),
      };
    }
    const { hosting } = connector;
    const { tokenSecretName, vault } = item;
    const cloud = cloudOf(hosting.type);
    const setup = toAccountSetup({
      id,
      render: () =>
        cloud.readGrant({
          connectorId: item.connector,
          hosting,
          id,
          secretName: tokenSecretName,
          vault,
        }),
    });
    if (setup) return setup;
    // The form disables the other cloud's vault, but leaves it chosen when it
    // was the default, and only the account's assembler refuses it. So an
    // account stored before that refusal can get here.
    return {
      help: (
        <Text type="warning">
          The connector {item.connector} runs on {cloud.label}, so it can&apos;t
          read a secret in {vaultLabel(vault.type)}. {REINSTALL.account} with{" "}
          {vaultLabel(cloud.vault)}.
        </Text>
      ),
    };
  };

export const launchdarklyInstaller: LaunchDarklyInstaller = {
  components: {
    connector: {
      instructions: connectorInstructions,
      items: {
        hosting: {
          labeler: () => "Where the connector runs.",
          options: {
            aws: {
              items: {
                accountId: installPicker({
                  description:
                    "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
                  integration: "aws",
                  missing:
                    "Install P0's AWS IAM management for the connector's account first. P0 lists only the accounts where it's installed.",
                }),
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Lambda function, which is also the name of the IAM role it runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The AWS region the connector runs in, such as us-west-2.",
                },
              },
            },
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, which is also the ID of the service account it runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: installPicker({
                  description:
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  integration: "gcloud",
                  missing:
                    "Install P0's Google Cloud IAM management for the connector's project first. P0 lists only the projects where it's installed.",
                }),
              },
            },
          },
        },
      },
      // On the new item's form, a component's labeler describes its id.
      labeler: () =>
        `A name of your choosing for this connector, unique among your LaunchDarkly connectors. Each LaunchDarkly account names the connector that reaches it by this ID. ${INVALID_ITEM_ID}`,
    },
    account: {
      instructions: accountInstructions,
      items: {
        connector: {
          optionProvider: connectorOptions,
          prerequisiteMessages: async (context) =>
            (await connectorOptions(context)).length === 0
              ? "Install a LaunchDarkly connector and finish its setup first. P0 doesn't list a connector that's still being set up."
              : undefined,
        },
        vault: {
          labeler: (context, _id, item) => vaultDescription(context, item),
          options: {
            "aws-sm": {
              items: {
                install: {
                  ...installPicker({
                    description:
                      "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
                    integration: "aws-sm",
                    missing:
                      "Install P0's AWS Secrets Manager integration for the secret's account first. P0 lists only the accounts where it's installed.",
                  }),
                  prerequisiteMessages: vaultPrerequisite("aws-sm"),
                },
              },
              prerequisiteMessages: vaultPrerequisite("aws-sm"),
            },
            "gcp-sm": {
              items: {
                install: {
                  ...installPicker({
                    description:
                      "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
                    integration: "gcp-sm",
                    missing:
                      "Install P0's Google Secret Manager integration for the secret's project first. P0 lists only the projects where it's installed.",
                  }),
                  prerequisiteMessages: vaultPrerequisite("gcp-sm"),
                },
              },
              prerequisiteMessages: vaultPrerequisite("gcp-sm"),
            },
          },
        },
      },
      labeler: () =>
        `A name of your choosing for this LaunchDarkly account, unique among your LaunchDarkly accounts. People name it when they request access, as in acme-prod/writer. ${INVALID_ITEM_ID}`,
    },
  },
};
